import { PolicyDefinition } from "./policyTypes";

export const polPeople: PolicyDefinition[] = [
  {
    id: "peo-dignity",
    code: "PEO-001",
    title: "Dignity and Respect",
    category: "people",
    cqcFocus: "Caring",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "Every interaction protects privacy, dignity and individuality, including intimate care, personal choice and the way staff speak about residents.",
    keyPoints: [
      "Staff knock and wait before entering rooms.",
      "Residents are addressed by their preferred name and title.",
      "Privacy is protected during all personal care.",
      "Undignified language or behaviour is challenged immediately.",
    ],
    relatedRegulations: [
      "Regulation 9 Person Centred Care",
      "Regulation 10 Dignity and Respect",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Knock and wait before entering any room.",
        "2. Use the preferred name and title recorded in the care plan.",
        "3. Close doors and curtains during personal care.",
        "4. Explain what you are going to do before doing it, and get consent.",
        "5. Keep the person covered as much as possible at all times.",
        "6. Respect same sex care preferences wherever practicable.",
        "7. Challenge undignified language or behaviour on the spot.",
        "8. Complete dignity training at induction and annually; keep a named dignity champion.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Every staff member models dignified care.",
        "Dignity champion leads refreshers and spot checks.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Annual resident and family dignity survey.",
        "Dignity spot checks twice a year.",
      ]},
    ],
  },
  {
    id: "peo-equality",
    code: "PEO-002",
    title: "Equality, Diversity and Human Rights",
    category: "people",
    cqcFocus: "Caring",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures fair treatment for residents, staff and visitors, with reasonable adjustments made and recorded for every protected characteristic.",
    keyPoints: [
      "No discrimination, harassment or victimisation is tolerated.",
      "Reasonable adjustments are assessed and recorded on admission.",
      "Information is provided in accessible formats.",
      "Culture, faith and belief are respected in daily life.",
    ],
    relatedRegulations: [
      "Equality Act 2010",
      "Human Rights Act 1998",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Assess and record reasonable adjustments on admission.",
        "2. Arrange interpreters or advocates whenever needed.",
        "3. Adapt menus, activities and worship to culture and belief.",
        "4. Report and address any discriminatory behaviour immediately.",
        "5. Deliver equality training at induction.",
        "6. Review equality themes at the quarterly meeting.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Managers act on every reported issue.",
        "All staff complete equality training.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Annual equality audit.",
        "Complaints monitored for equality themes.",
      ]},
    ],
  },
  {
    id: "peo-communication",
    code: "PEO-003",
    title: "Communication and Accessible Information",
    category: "people",
    cqcFocus: "Responsive",
    sop: false,
    version: "2.0",
    reviewMonths: 12,
    summary: "Ensures every resident can communicate their needs through a personal communication profile, maintained aids and staff who use the preferred method every time.",
    keyPoints: [
      "A communication profile is completed on admission.",
      "Hearing aids, glasses and call bells are checked and labelled.",
      "Information is available in large print and easy read.",
      "Staff use the preferred method of communication every time.",
    ],
    relatedRegulations: [
      "Accessible Information Standard",
      "Regulation 9 Person Centred Care",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete a communication profile on admission with family input.",
        "2. Label and maintain hearing aids, glasses and aids; check batteries weekly.",
        "3. Provide information in accessible formats such as large print or easy read.",
        "4. Use the preferred method every time, including non-verbal cues.",
        "5. Involve speech and language therapy where needed.",
        "6. Review the profile after any change in ability.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Care staff keep profiles current.",
        "Activities coordinator supports communication in sessions.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly audit of communication profiles.",
        "Aid checks recorded weekly.",
      ]},
    ],
  },
  {
    id: "peo-activities",
    code: "PEO-004",
    title: "Activities and Meaningful Occupation",
    category: "people",
    cqcFocus: "Responsive",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures every resident has access to meaningful activities that matches their interests, history and abilities, with outcomes recorded and reviewed.",
    keyPoints: [
      "Activities are person centred and based on life history.",
      "Residents choose what to do and when to do it.",
      "Outcomes and enjoyment are recorded.",
      "Activities coordinator plans a varied monthly programme.",
    ],
    relatedRegulations: [
      "Regulation 9 Person Centred Care",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete an activities profile with each resident on admission.",
        "2. Plan a varied monthly programme across cognitive, physical and social domains.",
        "3. Offer choice every day; participation is never forced.",
        "4. Record outcomes and enjoyment for each session.",
        "5. Review preferences quarterly and after any change.",
        "6. Involve family in planning where appropriate.",
        "7. Link activities to care plan goals.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Activities coordinator plans and leads sessions.",
        "All staff support residents to participate.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly activities programme review.",
        "Quarterly resident satisfaction feedback.",
      ]},
    ],
  },
  {
    id: "peo-advocacy",
    code: "PEO-005",
    title: "Advocacy and Representation",
    category: "people",
    cqcFocus: "Responsive",
    sop: false,
    version: "2.0",
    reviewMonths: 12,
    summary: "Ensures residents who struggle to express their wishes have access to independent advocacy and that their voice shapes their care and the service.",
    keyPoints: [
      "Independent advocacy is offered to those who need it.",
      "Residents are supported to participate in decisions about their care.",
      "Resident meetings capture feedback and lead to changes.",
      "Advocates are welcomed and given access to records.",
    ],
    relatedRegulations: [
      "Care Act 2014",
      "Regulation 9 Person Centred Care",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Assess advocacy need on admission and at review.",
        "2. Refer to independent advocacy services where criteria are met.",
        "3. Support residents to express views and participate in decisions.",
        "4. Hold resident meetings monthly and act on feedback.",
        "5. Give advocates access to records and staff.",
        "6. Record how resident feedback shaped changes to care or the service.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Key workers assess need and coordinate referrals.",
        "Manager acts on resident meeting outcomes.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Advocacy referrals tracked quarterly.",
        "Resident meeting actions followed to closure.",
      ]},
    ],
  },
  {
    id: "peo-family",
    code: "PEO-006",
    title: "Family and Visitor Support",
    category: "people",
    cqcFocus: "Caring",
    sop: false,
    version: "2.0",
    reviewMonths: 12,
    summary: "Welcomes family and friends, supports them to stay involved, and manages any restriction in the best interests of the resident and other residents.",
    keyPoints: [
      "Family are welcomed at any time unless restricted for safety.",
      "Communication preferences are recorded and shared with staff.",
      "Restrictions are recorded, reviewed and justified.",
      "Family complaints are heard and acted on.",
    ],
    relatedRegulations: [
      "Regulation 9 Person Centred Care",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Welcome family and friends at any reasonable time.",
        "2. Record communication preferences and share with all staff.",
        "3. Keep family informed about changes, incidents and reviews.",
        "4. Record any restriction on visits with the reason and review date.",
        "5. Listen to family concerns and act on them.",
        "6. Offer family involvement in care planning and reviews.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "All staff welcome visitors warmly.",
        "Manager reviews any restrictions quarterly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Annual family satisfaction survey.",
        "Restrictions reviewed quarterly.",
      ]},
    ],
  },
  {
    id: "peo-personal-care",
    code: "PEO-007",
    title: "Personal Care and Appearance",
    category: "people",
    cqcFocus: "Caring",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Ensures residents are clean, comfortable and presented as they wish, with personal grooming preferences recorded and followed every day.",
    keyPoints: [
      "Personal care is given with dignity and privacy.",
      "Clothing and grooming preferences are recorded and followed.",
      "Laundry is returned clean and in good repair.",
      "Personal items are labelled and protected from loss.",
    ],
    relatedRegulations: [
      "Regulation 9 Person Centred Care",
      "Regulation 10 Dignity and Respect",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Record grooming and clothing preferences on admission.",
        "2. Give personal care with privacy and dignity every time.",
        "3. Offer choice of clothing and hairstyle each day.",
        "4. Return laundry clean, folded and in good repair.",
        "5. Label personal items and report loss immediately.",
        "6. Support residents to maintain their own appearance where they wish.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Care staff deliver personal care to preference.",
        "Laundry staff return items same day where possible.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly personal care spot check.",
        "Laundry turnaround time audited weekly.",
      ]},
    ],
  },
];